'use client'

import { useEffect, useState } from 'react'
import PageBanner from '@/components/features/PageBanner'
import Link from 'next/link'
import { Download } from 'lucide-react'

interface News {
  id: number
  title: string
  content: string
  created_at: string
  file_url: string | null
  type: string
  files: { id: number; file_url: string }[]
}

export default function NewsPage() {
  const [news, setNews] = useState<News[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredNews, setFilteredNews] = useState<News[]>([])

  useEffect(() => {
    fetchNews()
  }, [])

  useEffect(() => {
    filterNews()
  }, [searchTerm, news])

  const fetchNews = async () => {
    try {
      const res = await fetch('/api/news')
      const data = await res.json()
      if (data.success) {
        setNews(data.news)
        setFilteredNews(data.news)
      }
    } catch (error) {
      console.error('Error fetching news:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterNews = () => {
    const searchTermLower = searchTerm.toLowerCase().trim()
    if (!searchTermLower) {
      setFilteredNews(news)
      return
    }

    const filtered = news.filter(item => 
      item.title.toLowerCase().includes(searchTermLower) ||
      item.content.toLowerCase().includes(searchTermLower)
    )
    setFilteredNews(filtered)
  }

  const handleSearch = () => {
    filterNews()
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50">
        <PageBanner 
          title="最新消息" 
          subtitle="掌握最新資訊與公告"
        />
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">載入中...</div>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-gray-50 pt-24">
      <PageBanner 
        title="最新消息" 
        subtitle="News"
      />
      
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="max-w-2xl mx-auto flex gap-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="搜尋標題或內容..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              搜尋
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {filteredNews.length > 0 ? (
            filteredNews.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-4 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    item.type === 'news' 
                      ? 'bg-blue-100 text-blue-800' 
                      : item.type === 'announcement' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {item.type === 'news' ? '新聞' : item.type === 'announcement' ? '公告' : '公文'}
                  </span>
                  <h2 className="text-xl font-bold text-gray-900 flex-grow">{item.title}</h2>
                  <span className="text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex justify-between items-start gap-4">
                  <div>
                    <Link
                      href={`/news/${item.id}`}
                      className="inline-block text-teal-600 hover:text-teal-700"
                    >
                      閱讀更多
                    </Link>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-end">
                    {item.files && item.files.length > 0 ? (
                      item.files.map((file, index) => (
                        <a
                          key={file.id}
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          檔案{index + 1}
                        </a>
                      ))
                    ) : (
                      item.file_url && (
                        <a
                          href={item.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          檔案1
                        </a>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-8">
              找不到符合的新聞
            </div>
          )}
        </div>
      </div>
    </main>
  )
} 